import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useIndoStore } from "./indo";

function generateBannersHash(listBanners) {
  return listBanners
    .map((b) => `${b.NOM_NEWS}|${b.TIPO}|${b.LINKPROMO}|${b.DATAB}|${b.DATABI}|${b.VKORG}`)
    .join(";;");
}

function isBannerValid(banner, currentDate) {
  try {
    const dateFrom = new Date(banner.DATAB);
    const dateTo = new Date(banner.DATABI);
    if (isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) return false;
    return currentDate >= dateFrom && currentDate <= dateTo;
  } catch {
    return false;
  }
}

function isBannerVKORGValid(banner, listaAreasCliente) {
  try {
    if (!listaAreasCliente || listaAreasCliente.length === 0) return true;
    if (!banner.VKORG) return false;
    const codigosOrgVenta = listaAreasCliente.map((a) => a.ORG_VENTA).filter(Boolean);
    return codigosOrgVenta.includes(banner.VKORG);
  } catch {
    return false;
  }
}

export const useBannersStore = defineStore("banners", () => {
  const bannerQueues = ref({});
  const currentIndices = ref({});
  const assignedBanners = ref({});
  const activeSlots = ref({});
  const queuesInitialized = ref(false);
  const lastInitialized = ref(null);
  const lastBannersHash = ref(null);

  function resetQueues() {
    bannerQueues.value = {};
    currentIndices.value = {};
    assignedBanners.value = {};
    activeSlots.value = {};
    queuesInitialized.value = false;
    lastInitialized.value = null;
    lastBannersHash.value = null;
  }

  function resetActiveSlots() {
    activeSlots.value = {};
  }

  async function initializeBannerQueues() {
    const indoStore = useIndoStore();
    const listBanners = indoStore.profile?.AreaVenta?.listBANNERS || [];
    const listaAreasCliente = indoStore.profile?.Areaventa?.listaAreasCliente || [];

    if (listBanners.length === 0) {
      resetQueues();
      return;
    }

    const currentHash = generateBannersHash(listBanners);
    const hasChanged = currentHash !== lastBannersHash.value;

    if (queuesInitialized.value && Object.keys(bannerQueues.value).length > 0 && !hasChanged) {
      resetActiveSlots();
      return;
    }

    const newQueues = {};
    const now = new Date();

    listBanners.forEach((banner) => {
      if (banner.TIPO === "N" || banner.TIPO === "P2") return;
      if (!isBannerValid(banner, now)) return;
      if (!isBannerVKORGValid(banner, listaAreasCliente)) return;
      if (!newQueues[banner.TIPO]) newQueues[banner.TIPO] = [];
      newQueues[banner.TIPO].push(banner);
    });

    bannerQueues.value = newQueues;
    lastInitialized.value = new Date().toISOString();

    const initialIndices = {};
    Object.keys(newQueues).forEach((tipo) => {
      initialIndices[tipo] = 0;
    });
    currentIndices.value = initialIndices;

    queuesInitialized.value = true;
    lastBannersHash.value = currentHash;
  }

  function getCurrentBanner(tipo) {
    const queue = bannerQueues.value[tipo] || [];
    const idx = currentIndices.value[tipo] || 0;
    if (idx >= queue.length) return null;
    return queue[idx];
  }

  function getNextBanner(tipo) {
    const queue = bannerQueues.value[tipo] || [];
    const idx = currentIndices.value[tipo] || 0;
    if (idx >= queue.length) return null;
    const banner = queue[idx];
    if (!currentIndices.value[tipo]) currentIndices.value[tipo] = 0;
    currentIndices.value[tipo]++;
    return banner;
  }

  function getBannerForSlot({ tipo, slotId }) {
    const slotKey = `${tipo}-${slotId}`;
    if (assignedBanners.value[slotKey]) return assignedBanners.value[slotKey];
    const queue = bannerQueues.value[tipo] || [];
    const idx = currentIndices.value[tipo] || 0;
    if (idx >= queue.length) return null;
    const banner = queue[idx];
    assignedBanners.value[slotKey] = banner;
    if (!currentIndices.value[tipo]) currentIndices.value[tipo] = 0;
    currentIndices.value[tipo]++;
    return banner;
  }

  function releaseBannerFromSlot({ tipo, slotId }) {
    delete assignedBanners.value[`${tipo}-${slotId}`];
  }

  function reserveSlot(tipo) {
    const slots = activeSlots.value[tipo] || [];
    let slotId = 0;
    while (slots.includes(slotId)) slotId++;
    if (!activeSlots.value[tipo]) activeSlots.value[tipo] = [];
    activeSlots.value[tipo].push(slotId);
    return slotId;
  }

  function releaseSlot({ tipo, slotId }) {
    if (activeSlots.value[tipo]) {
      const idx = activeSlots.value[tipo].indexOf(slotId);
      if (idx > -1) activeSlots.value[tipo].splice(idx, 1);
    }
  }

  function getQueueByType(tipo) {
    return bannerQueues.value[tipo] || [];
  }
  function getBannersByType(tipo) {
    return bannerQueues.value[tipo] || [];
  }

  const areQueuesInitialized = computed(() => queuesInitialized.value);
  const getQueueCounts = computed(() => {
    const counts = {};
    Object.keys(bannerQueues.value).forEach((tipo) => {
      counts[tipo] = bannerQueues.value[tipo].length;
    });
    return counts;
  });

  return {
    bannerQueues,
    currentIndices,
    assignedBanners,
    activeSlots,
    queuesInitialized,
    lastInitialized,
    lastBannersHash,
    resetQueues,
    resetActiveSlots,
    initializeBannerQueues,
    getCurrentBanner,
    getNextBanner,
    getBannerForSlot,
    releaseBannerFromSlot,
    reserveSlot,
    releaseSlot,
    getQueueByType,
    getBannersByType,
    areQueuesInitialized,
    getQueueCounts,
  };
});
